<template>
  <section class="context-history-view tw-card">
    <p class="section-eyebrow">Context history</p>

    <div v-if="isLoading" class="tw-state">
      <b-loading :is-full-page="false" :active="true" />
      <p>Loading version history...</p>
    </div>

    <div v-else-if="loadError" class="tw-state tw-state--error">
      <p>{{ loadError }}</p>
      <b-button type="is-light" @click="load">Retry</b-button>
    </div>

    <div v-else-if="versions.length < 2" class="history-empty">
      <p>Not enough context history yet to compare versions.</p>
    </div>

    <template v-else>
      <div class="history-pickers">
        <b-field label="From">
          <b-select v-model="fromVersion">
            <option v-for="v in versions" :value="v.version" :key="`from-${v.version}`">
              Version {{ v.version }} — {{ formatDate(v.createdAt) }}
            </option>
          </b-select>
        </b-field>
        <b-field label="To">
          <b-select v-model="toVersion">
            <option v-for="v in versions" :value="v.version" :key="`to-${v.version}`">
              Version {{ v.version }} — {{ formatDate(v.createdAt) }}
            </option>
          </b-select>
        </b-field>
      </div>

      <div v-if="isDiffLoading" class="tw-state">
        <b-loading :is-full-page="false" :active="true" />
        <p>Comparing versions...</p>
      </div>

      <template v-else>
        <div v-if="rolledUpDiffError" class="tw-state tw-state--error">
          <p>{{ rolledUpDiffError }}</p>
        </div>

        <template v-else-if="rolledUpDiff">
          <div v-if="!metadataFullyAvailable" class="tw-state tw-state--error">
            <p>
              Evidence metadata is missing for
              {{ missingMetadataSideLabel }}, so confidence and classification changes
              can't be compared for this pair. Text changes below are still accurate.
            </p>
          </div>

          <div v-if="!hasAnyMeaningfulChange" class="history-empty">
            <p>No meaningful change between these two versions.</p>
          </div>

          <template v-else>
            <div v-if="changedProfileFields.length" class="history-section">
              <p class="section-eyebrow">Changed profile fields</p>
              <div
                v-for="field in changedProfileFields"
                :key="field.fieldPath"
                class="context-item"
              >
                <div class="context-item__header">
                  <h4>{{ dimensionLabel(field.groupKey) }} — {{ dimensionLabel(field.fieldName) }}</h4>
                  <div class="context-item__badges">
                    <b-tag
                      v-for="shift in categoricalShifts(field)"
                      :key="shift.key"
                      :type="shift.tagType"
                    >
                      {{ shift.label }}: {{ shift.from }} → {{ shift.to }}
                    </b-tag>
                  </div>
                </div>
                <dl class="context-item__meta">
                  <div>
                    <dt>From</dt>
                    <dd>{{ formatValue(field.fromValue) }}</dd>
                  </div>
                  <div>
                    <dt>To</dt>
                    <dd>{{ formatValue(field.toValue) }}</dd>
                  </div>
                  <template v-if="contradictionShift(field)">
                    <div>
                      <dt>Contradiction — from</dt>
                      <dd>{{ contradictionShift(field).from }}</dd>
                    </div>
                    <div>
                      <dt>Contradiction — to</dt>
                      <dd>{{ contradictionShift(field).to }}</dd>
                    </div>
                  </template>
                </dl>
              </div>
            </div>

            <div v-if="textUpdatedFields.length" class="history-section">
              <p class="section-eyebrow">Text updated (no change in confidence or evidence)</p>
              <div
                v-for="field in textUpdatedFields"
                :key="`text-${field.fieldPath}`"
                class="context-item context-item--subtle"
              >
                <div class="context-item__header">
                  <h4>{{ dimensionLabel(field.groupKey) }} — {{ dimensionLabel(field.fieldName) }}</h4>
                  <b-tag type="is-light">text updated</b-tag>
                </div>
                <dl class="context-item__meta">
                  <div>
                    <dt>From</dt>
                    <dd>{{ formatValue(field.fromValue) }}</dd>
                  </div>
                  <div>
                    <dt>To</dt>
                    <dd>{{ formatValue(field.toValue) }}</dd>
                  </div>
                </dl>
              </div>
            </div>

            <div class="history-section">
              <p class="section-eyebrow">Executive summary</p>
              <p v-if="!executiveSummaryDiff.changed" class="history-empty">No change.</p>
              <div v-else class="context-item">
                <dl
                  v-for="line in executiveSummaryLines"
                  :key="line.key"
                  class="context-item__meta"
                >
                  <div>
                    <dt>{{ line.label }} — from</dt>
                    <dd>{{ line.from }}</dd>
                  </div>
                  <div>
                    <dt>{{ line.label }} — to</dt>
                    <dd>{{ line.to }}</dd>
                  </div>
                </dl>
              </div>
            </div>

            <div v-if="hasCriticalEvidenceChanges" class="history-section">
              <p class="section-eyebrow">Critical evidence changes</p>
              <div
                v-for="(item, index) in criticalEvidenceEntries"
                :key="`critical-${index}`"
                class="confirmation-item"
              >
                <div class="context-item__header">
                  <h4>{{ dimensionLabel(item.dimensionKey) }}</h4>
                  <div class="context-item__badges">
                    <b-tag type="is-warning">{{ item.changeType }}</b-tag>
                    <b-tag>{{ item.tier }}</b-tag>
                  </div>
                </div>
                <p class="context-item__conclusion">{{ item.detail }}</p>
              </div>
            </div>
          </template>
        </template>

        <details class="history-dimensions">
          <summary class="section-eyebrow">Dimension detail</summary>

          <div v-if="dimensionDiffError" class="tw-state tw-state--error">
            <p>{{ dimensionDiffError }}</p>
          </div>

          <div v-else-if="!dimensionDiff" class="history-empty">
            <p>Dimension detail isn't available for this comparison.</p>
          </div>

          <template v-else>
            <div
              v-for="group in dimensionGroups"
              :key="group.tier"
              class="history-section"
            >
              <p class="section-eyebrow">{{ group.label }}</p>
              <p v-if="!group.entries.length" class="history-empty">
                No {{ group.label.toLowerCase() }} dimensions recorded for this comparison.
              </p>
              <div
                v-for="entry in group.entries"
                :key="`dim-${group.tier}-${entry.dimensionKey}`"
                class="context-item"
              >
                <div class="context-item__header">
                  <h4>{{ dimensionLabel(entry.dimensionKey) }}</h4>
                  <div class="context-item__badges">
                    <b-tag :type="dimensionTagType(entry)">{{ dimensionTagLabel(entry) }}</b-tag>
                    <b-tag
                      v-for="shift in categoricalShifts(entry)"
                      :key="shift.key"
                      :type="shift.tagType"
                    >
                      {{ shift.label }}: {{ shift.from }} → {{ shift.to }}
                    </b-tag>
                  </div>
                </div>
                <dl v-if="entry.changed || entry.valueChanged" class="context-item__meta">
                  <div>
                    <dt>From</dt>
                    <dd>{{ entry.from ? formatValue(entry.from.conclusion) : '—' }}</dd>
                  </div>
                  <div>
                    <dt>To</dt>
                    <dd>{{ entry.to ? formatValue(entry.to.conclusion) : '—' }}</dd>
                  </div>
                  <template v-if="contradictionShift(entry)">
                    <div>
                      <dt>Contradiction — from</dt>
                      <dd>{{ contradictionShift(entry).from }}</dd>
                    </div>
                    <div>
                      <dt>Contradiction — to</dt>
                      <dd>{{ contradictionShift(entry).to }}</dd>
                    </div>
                  </template>
                </dl>
              </div>
            </div>
          </template>
        </details>
      </template>
    </template>
  </section>
</template>

<script>
import {
  fetchCompanyContextHistory,
  fetchCompanyContextDiff,
  fetchCompanyContextDimensionDiff,
} from '@/services/companyContextService'

export default {
  name: 'ContextHistoryView',
  data() {
    return {
      isLoading: false,
      loadError: null,
      versions: [],
      fromVersion: null,
      toVersion: null,
      isDiffLoading: false,
      rolledUpDiff: null,
      rolledUpDiffError: null,
      dimensionDiff: null,
      dimensionDiffError: null,
      suppressDiffWatch: false,
      diffRequestId: 0,
    }
  },
  computed: {
    changedProfileFields() {
      return (this.rolledUpDiff?.profileFields || []).filter(field => field.changed)
    },
    // The lower tier: the value was rewritten but no categorical field moved. Surfaced
    // separately so a text-only rewrite is visible without being ranked as a real shift.
    textUpdatedFields() {
      return (this.rolledUpDiff?.profileFields || []).filter(field => !field.changed && field.valueChanged)
    },
    metadataAvailable() {
      return this.rolledUpDiff?.metadataAvailable || { from: true, to: true }
    },
    metadataFullyAvailable() {
      return this.metadataAvailable.from && this.metadataAvailable.to
    },
    missingMetadataSideLabel() {
      const { from, to } = this.metadataAvailable
      if (!from && !to) return 'both versions'
      return !from ? 'the earlier version' : 'the later version'
    },
    executiveSummaryDiff() {
      return this.rolledUpDiff?.executiveSummary || { changed: false, from: null, to: null }
    },
    executiveSummaryLines() {
      const { from, to } = this.executiveSummaryDiff
      return [
        { key: 'profile', label: 'Customer profile' },
        { key: 'baseline', label: 'Operating baseline' },
        { key: 'workflowRules', label: 'Workflow rules' },
      ]
        .map(line => ({
          ...line,
          from: this.formatValue(from ? from[line.key] : null),
          to: this.formatValue(to ? to[line.key] : null),
        }))
        .filter(line => line.from !== line.to)
    },
    dimensionGroups() {
      const dimensions = this.dimensionDiff?.dimensions || []
      return [
        { tier: 'profile', label: 'Customer profile', entries: dimensions.filter(d => d.tier === 'profile') },
        { tier: 'baseline', label: 'Operating baseline', entries: dimensions.filter(d => d.tier === 'baseline') },
      ]
    },
    criticalEvidenceEntries() {
      const criticalEvidence = this.rolledUpDiff?.criticalEvidence || { added: [], removed: [], changed: [] }
      return [
        ...(criticalEvidence.added || []).map(item => ({ ...item, changeType: 'added' })),
        ...(criticalEvidence.removed || []).map(item => ({ ...item, changeType: 'removed' })),
        ...(criticalEvidence.changed || []).map(item => ({ ...item, changeType: 'changed' })),
      ]
    },
    hasCriticalEvidenceChanges() {
      return this.criticalEvidenceEntries.length > 0
    },
    // Includes the dimension-level diff (which covers Operating Baseline, absent from the
    // rolled-up diff entirely) and the text-updated tier — otherwise "No meaningful change"
    // could render directly above a drill-down panel full of changed baseline dimensions.
    hasAnyMeaningfulChange() {
      return (
        this.changedProfileFields.length > 0
        || this.textUpdatedFields.length > 0
        || this.executiveSummaryDiff.changed
        || this.hasCriticalEvidenceChanges
        || this.changedDimensionCount > 0
      )
    },
    changedDimensionCount() {
      return (this.dimensionDiff?.dimensions || []).filter(entry => entry.changed || entry.valueChanged).length
    },
  },
  watch: {
    fromVersion() {
      if (this.suppressDiffWatch) return
      this.fetchDiffs()
    },
    toVersion() {
      if (this.suppressDiffWatch) return
      this.fetchDiffs()
    },
  },
  created() {
    this.load()
  },
  methods: {
    getToken() {
      return this.$cookies.get('teamwrkr-token')
    },
    formatDate(value) {
      if (!value) return '—'
      return this.$moment(value).format('MMM D, YYYY')
    },
    formatValue(value) {
      if (value === null || value === undefined || value === '') return '—'
      return Array.isArray(value) ? value.join(', ') : `${value}`
    },
    dimensionLabel(value) {
      return `${value || ''}`
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, char => char.toUpperCase())
        .trim()
    },
    classificationTagType(classification) {
      switch (classification) {
        case 'observed': return 'is-success'
        case 'inferred': return 'is-info'
        case 'needs_confirmation': return 'is-warning'
        case 'unknown': return 'is-light'
        default: return 'is-light'
      }
    },
    // `changed` is driven by four categorical fields, but only three of them are short,
    // enum-like values that read well as a compact tag ("confidence: low → high").
    // contradictionStatus is a full sentence written by the contradiction-detection pass
    // (see workSnowflake.repository.js's column comment) — cramming that into a tag doesn't
    // wrap the way a tag is meant to, it just produces one very long pill. Handled separately
    // by contradictionShift() below, rendered as ordinary wrapped text instead.
    categoricalShifts(entry) {
      const from = entry.fromMeta || entry.from || {}
      const to = entry.toMeta || entry.to || {}
      return [
        { key: 'evidenceClassification', label: 'evidence' },
        { key: 'confidence', label: 'confidence' },
        { key: 'confirmationStatus', label: 'confirmation' },
      ]
        .map(field => ({
          ...field,
          from: from[field.key] ?? '—',
          to: to[field.key] ?? '—',
        }))
        .filter(field => field.from !== field.to)
        .map(field => ({
          ...field,
          tagType: field.key === 'evidenceClassification'
            ? this.classificationTagType(field.to)
            : 'is-info',
        }))
    },
    // Long-form counterpart to categoricalShifts — same "did contradictionStatus change"
    // check, but returns the two full strings for wrapped dt/dd rendering, not a tag.
    contradictionShift(entry) {
      const from = entry.fromMeta || entry.from || {}
      const to = entry.toMeta || entry.to || {}
      const fromValue = from.contradictionStatus ?? null
      const toValue = to.contradictionStatus ?? null
      if (fromValue === toValue) return null
      return { from: fromValue || 'No contradiction noted', to: toValue || 'No contradiction noted' }
    },
    dimensionTagType(entry) {
      if (entry.changed) return 'is-warning'
      return entry.valueChanged ? 'is-light' : 'is-light'
    },
    dimensionTagLabel(entry) {
      if (entry.changed) return 'changed'
      return entry.valueChanged ? 'text updated' : 'unchanged'
    },
    resetDiffState() {
      this.rolledUpDiff = null
      this.rolledUpDiffError = null
      this.dimensionDiff = null
      this.dimensionDiffError = null
    },
    async load() {
      this.isLoading = true
      this.loadError = null
      this.resetDiffState()
      try {
        const versions = await fetchCompanyContextHistory(this.axios, this.getToken())
        this.versions = versions
        if (versions.length >= 2) {
          // Assign both selections with the watchers suppressed, then fetch exactly once.
          // Letting each assignment trigger its own watcher (plus the explicit call below)
          // fired fetchDiffs three times per mount — six HTTP requests, six Snowflake
          // queries — and left isDiffLoading cleared by whichever landed first.
          this.suppressDiffWatch = true
          this.toVersion = versions[0].version
          this.fromVersion = versions[1].version
          await this.$nextTick()
          this.suppressDiffWatch = false
          await this.fetchDiffs()
        } else {
          this.fromVersion = null
          this.toVersion = null
        }
      } catch (error) {
        this.loadError = error?.response?.data?.message || 'Unable to load context history.'
      } finally {
        this.isLoading = false
      }
    },
    async fetchDiffs() {
      if (this.fromVersion === null || this.toVersion === null) return
      // Sequence token: a slower earlier request must not overwrite the results of a newer
      // selection that resolved first.
      this.diffRequestId += 1
      const requestId = this.diffRequestId
      const isStale = () => requestId !== this.diffRequestId

      this.isDiffLoading = true
      this.rolledUpDiffError = null
      this.dimensionDiffError = null

      const token = this.getToken()
      const [rolledUpResult, dimensionResult] = await Promise.allSettled([
        fetchCompanyContextDiff(this.axios, token, { from: this.fromVersion, to: this.toVersion }),
        fetchCompanyContextDimensionDiff(this.axios, token, { from: this.fromVersion, to: this.toVersion }),
      ])

      if (isStale()) return

      if (rolledUpResult.status === 'fulfilled') {
        this.rolledUpDiff = rolledUpResult.value
      } else {
        this.rolledUpDiff = null
        this.rolledUpDiffError = rolledUpResult.reason?.response?.data?.message || 'Unable to load the version comparison.'
      }

      if (dimensionResult.status === 'fulfilled') {
        this.dimensionDiff = dimensionResult.value
      } else {
        this.dimensionDiff = null
        this.dimensionDiffError = dimensionResult.reason?.response?.data?.message || 'Unable to load dimension detail.'
      }

      this.isDiffLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
// Review fix: same nested-grid min-width issue as AdminContextView.vue — every level here
// is a grid item of the one above it, and a grid item's default min-width is its content's
// natural width, not 0. Fixing only the text-level elements (dd/h4 below) wasn't enough; the
// card containing them just overflowed instead. This component's own root is itself a grid
// item of AdminContextView.vue's `.admin-context-view` grid (it's mounted as a section
// inside it), so it needs min-width: 0 on itself too, not just on its own children.
.context-history-view {
  display: grid;
  gap: $space-4;
  min-width: 0;
}

.history-pickers {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: $space-4;
  min-width: 0;
}

.history-empty {
  color: $color-text-muted;
}

.history-section {
  display: grid;
  gap: $space-3;
  min-width: 0;
}

.history-dimensions {
  border-top: 1px solid $color-border-muted;
  padding-top: $space-4;
  min-width: 0;
}

.history-dimensions summary {
  cursor: pointer;
}

.context-item {
  border: 1px solid $color-border;
  border-radius: $radius-lg;
  background: $color-surface;
  padding: $space-4;
  display: grid;
  gap: $space-3;
  min-width: 0;
}

.context-item--subtle {
  background: $color-surface-soft;
  border-style: dashed;
}

.context-item__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $space-3;
  flex-wrap: wrap;

  h4 {
    margin: 0;
    // Flex/grid items don't shrink below their content's natural width by default
    // (min-width: auto) — without this, a long title just pushes past the card and the
    // page overflows sideways instead of wrapping onto a new line.
    min-width: 0;
    flex: 1 1 240px;
    overflow-wrap: anywhere;
    font-size: $font-size-md;
  }
}

.context-item__badges {
  display: flex;
  flex-wrap: wrap;
  gap: $space-2;
  flex-shrink: 0;
}

.context-item__conclusion {
  margin: 0;
  color: $color-text;
  line-height: 1.7;
  overflow-wrap: anywhere;
}

.context-item__meta {
  display: grid;
  // Review fix: was a multi-column auto-fit grid (fine when every value is short), but a
  // row can now mix short values (From/To) with a full paragraph (Contradiction — to) —
  // side by side, the short columns end up mostly empty white space next to one very tall
  // column, which reads worse than just stacking. Full-width, one row per pair.
  grid-template-columns: 1fr;
  gap: $space-3;
  margin: 0;

  // Same min-width: auto issue as above, on the grid item wrapping each dt/dd pair.
  > div {
    min-width: 0;
  }

  dt {
    color: $color-text-muted;
    font-size: $font-size-sm;
    font-weight: 700;
  }

  dd {
    margin: 0;
    color: $color-text;
    overflow-wrap: anywhere;
  }
}

.confirmation-item {
  border: 1px solid $color-border-muted;
  border-radius: $radius-lg;
  padding: $space-4;
  min-width: 0;
}
</style>
